import { checkIcon } from "../../elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";

/**
 * `RoadmapWidget` keeps track of the current step from a list
 * of steps. It is updated by using `setCurrentStepIndex`.
 *
 * @param {Renderable[]} stepTitles - Labels of all the different
 *   steps composing the road map
 * @param {function():integer} getCurrentStepIndex - Should return the
 *   current step index
 * @param {boolean} [hideLabels] - Whether the labels should be
 *   hidden
 */
export default class RoadmapWidget extends Widget {
	constructor({ stepTitles, getCurrentStepIndex, hideLabels = false }) {
		super(...arguments);

		this._stepTitles = stepTitles;
		this._getCurrentStepIndex = getCurrentStepIndex;
		this._hideLabels = hideLabels;
	}

	renderContentOn(html) {
		const currentStepIndex = this._getCurrentStepIndex();

		html.ul(
			{
				class: ["roadmap", { "hide-labels": this._hideLabels }],
				"data-test": "roadmap",
			},
			this._stepTitles.map((stepTitle, index) =>
				this._stepRenderer({ stepTitle, index, currentStepIndex }),
			),
		);
	}

	_stepRenderer({ stepTitle, index, currentStepIndex }) {
		return (html) =>
			html.li(
				{
					class: [
						"roadmap-step",
						{ "active-step": index === currentStepIndex },
					],
				},
				this._bulletRenderer({ index, currentStepIndex }),
				this._stepLabelRenderer(stepTitle),
			);
	}

	_bulletRenderer({ index, currentStepIndex }) {
		if (this._hideLabels) {
			return this._emptyBulletRenderer({ index, currentStepIndex });
		}

		return index < currentStepIndex
			? this._checkRenderer()
			: this._indexRenderer(index);
	}

	_emptyBulletRenderer({ index, currentStepIndex }) {
		const checked = index <= currentStepIndex;
		return (html) => html.div({ class: ["roadmap-bullet", { checked }] });
	}

	_checkRenderer() {
		return checkIcon({ class: "roadmap-bullet" });
	}

	_indexRenderer(index) {
		return (html) =>
			html.div({ class: "step-index roadmap-bullet" }, index + 1);
	}

	_stepLabelRenderer(stepTitle) {
		return (html) => {
			if (this._hideLabels) return;
			html.div({ class: "step-label" }, stepTitle);
		};
	}

	static forWizard({ wizard, hideLabels }) {
		const widget = new this({
			stepTitles: wizard.getStepTitles(),
			getCurrentStepIndex: () => wizard.getCurrentStepIndex(),
			hideLabels,
		});

		wizard.currentStepChanged.register(() => widget.update());

		return widget;
	}
}
